/**
 * Chat V2 — step view-model types.
 *
 * A "step" is one collapsible entry in the agent's reasoning trace (the
 * "Chat Steps" strip): a heading row — icon + label — that optionally expands
 * to a detail block. These are FE PRESENTATION types, deliberately separate
 * from the wire shape.
 */

import type { ComponentType, SVGProps } from 'react';
import type { ACTION_INTERRUPT_TYPE } from 'src/types/enum';

/**
 * The heading variant — resolves icon + default label via `stepHeadingRegistry`.
 * Values are FE-only display slugs; the wire→kind mapping (from `CHAT_STEP_NAME`
 * / `tool_name`) is a `steps/domains/` concern.
 */
export enum STEP_KIND {
  SEARCHING_WORKSPACE = 'searching_workspace',
  LISTING_DOCUMENTS = 'listing_documents',
  SEARCHING_WEB = 'searching_web',
  RUNNING_SCRIPT = 'running_script',
  EXECUTING_CODE = 'executing_code',
  CHECKING_IMAGE = 'checking_image',
  DEEP_RESEARCH = 'deep_research',
  CALLING_SUB_AGENT = 'calling_sub_agent',
  CHECKING_FILES = 'checking_files',
  TOOL_SEARCH = 'tool_search',
  INTERRUPT = 'interrupt',
  DONE = 'done',
  FAILED = 'failed'
}

/**
 * Lifecycle status of a step — orthogonal to its kind. Drives the heading's icon tone
 * (and, for `ACTIVE`, a spinner in place of the kind icon).
 */
export enum STEP_STATUS {
  PENDING = 'pending',
  ACTIVE = 'active',
  BLOCKED = 'blocked',
  DONE = 'done',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  ORPHANED = 'orphaned'
}

/**
 * The expanded-body variant — resolves the renderer via `stepDetailRegistry`.
 * One member per structurally-distinct detail block; tags and file chips are
 * sub-parts composed inside those blocks, not top-level detail kinds.
 */
export enum STEP_DETAIL_KIND {
  CODE = 'code',
  SEARCH = 'search',
  INTERRUPT = 'interrupt'
}

/** Where a search step looked — switches the result-row rendering. */
export enum STEP_SEARCH_SOURCE {
  WEB = 'web',
  ENTERPRISE = 'enterprise'
}

/** One row in a `SearchStepDetail`. */
export interface StepSearchResult {
  id: string;
  title: string;
  subtitle?: string;
  /** An enterprise hit's page. Rendered as translated copy, so it stays a NUMBER here. */
  page?: number;
  extension?: string;
  faviconUrl?: string;
}

interface StepDetailBase {
  kind: STEP_DETAIL_KIND;
}

/** Detail for `RUNNING_SCRIPT` / `EXECUTING_CODE` — a code block. */
export interface CodeStepDetail extends StepDetailBase {
  kind: STEP_DETAIL_KIND.CODE;
  tag?: string;
  language: string;
  input: string;
  output?: string;
}

/** Detail for `SEARCHING_WEB` / `SEARCHING_WORKSPACE` — a result list. */
export interface SearchStepDetail extends StepDetailBase {
  kind: STEP_DETAIL_KIND.SEARCH;
  source: STEP_SEARCH_SOURCE;
  results: StepSearchResult[];
}

/** Field control kind inside an interrupt form. */
export enum INTERRUPT_FIELD_KIND {
  TEXT = 'text',
  TEXTAREA = 'textarea'
}

/**
 * One field in an interrupt form. `id` doubles as the react-hook-form field
 * name. Fields are BE-driven, so the form renders whatever the interrupt sends.
 */
export interface InterruptField {
  id: string;
  label: string;
  fieldKind: INTERRUPT_FIELD_KIND;
  value?: string;
  placeholder?: string;
  hint?: string;
}

/**
 * Detail for a `STEP_KIND.INTERRUPT` step: a tool-approval card the user edits
 * then Confirms or Denies.
 */
export interface InterruptStepDetail extends StepDetailBase {
  kind: STEP_DETAIL_KIND.INTERRUPT;
  interruptType: ACTION_INTERRUPT_TYPE;
  title: string;
  logoIcon?: ComponentType<SVGProps<SVGSVGElement>>;
  heading: string;
  description?: string;
  fields: InterruptField[];
  confirmLabel?: string;
  denyLabel?: string;
}

/** Discriminated on `kind` — the union `stepDetailRegistry` resolves. */
export type StepDetail =
  | CodeStepDetail
  | SearchStepDetail
  | InterruptStepDetail;

/**
 * The agent's pause, verbatim enough to resolve it.
 */
export interface StepInteraction {
  type: ACTION_INTERRUPT_TYPE;
  toolName?: string;
  args?: Record<string, unknown>;
  toolInfo?: Record<string, unknown>;
  state?: unknown;
}

/**
 * One step — BOTH the store's model and the renderer's view model, deliberately ONE
 * type.
 */
export interface Step {
  id: string;
  domain?: string;
  kind: STEP_KIND;
  status: STEP_STATUS;
  /** A translation key — resolved with `t()` by the renderer, never pre-translated. */
  label?: string;
  /** The tool's own input, appended verbatim after the translated `label`. */
  labelQuery?: string;
  detail?: StepDetail;

  parentKey?: string;

  runId?: string;
  toolCallId?: string;

  interaction?: StepInteraction;

  inputPartial?: Record<string, unknown>;
  outputText?: string;
  sources?: unknown[];

  startedAt?: number;
  endedAt?: number;

  lateCreated?: boolean;

  version?: number;
  ordinal?: number;
}

/**
 * Is this step waiting on the USER rather than on the agent?
 */
export function isAwaitingUser(step: Pick<Step, 'status'>): boolean {
  return step.status === STEP_STATUS.BLOCKED;
}
